import type { VirtualClipModel } from './virtualClipModel';
import { isObjectV2 } from '@mimir/primitive/type-guards/isObject';
import type { ItemAccessControl } from './itemAccessControlDto';

export const isInstanceOfVirtualClip = (obj: unknown): obj is VirtualClipModel =>
  isObjectV2(obj)
    && 'masterClipId' in obj
    && 'inPoint' in obj
    && 'outPoint' in obj
    && typeof obj['masterClipId'] === 'string'
    && typeof obj['inPoint'] === 'number'
    && typeof obj['outPoint'] === 'number';

export type CreateVirtualClipPayload = Omit<VirtualClipModel, 'outPoint'> & {
  outPoint?: number;
  visibleTo?: ItemAccessControl;
  updatableBy?: ItemAccessControl;
};

export const isInstanceOfCreateVirtualClipPayload = (obj: unknown): obj is CreateVirtualClipPayload => {
  if (!isObjectV2(obj)) {
    return false;
  }
  const payload = obj as Partial<CreateVirtualClipPayload>;
  return typeof payload.masterClipId === 'string'
    && typeof payload.inPoint === 'number'
    && (!payload.outPoint || typeof payload.outPoint === 'number');
};

export const requiredKeysForVirtualClipCreation: readonly (keyof CreateVirtualClipPayload)[] = <const>['masterClipId', 'inPoint'];
export const isPartialCreateVirtualClipPayload = (obj: unknown): obj is Partial<CreateVirtualClipPayload> => {
  if (!isObjectV2(obj)) {
    return false;
  }
  return !!(Object.keys(obj).find(k => requiredKeysForVirtualClipCreation.includes(k as keyof CreateVirtualClipPayload)));
};

export const isVirtualClip = (virtualClip: VirtualClipModel | undefined): virtualClip is VirtualClipModel =>
  isInstanceOfVirtualClip(virtualClip) && !!virtualClip.masterClipId;

export interface UpdateVirtualClipPayload {
  inPoint?: number;
  outPoint?: number;
}

export const isInstanceOfUpdateVirtualClipPayload = (obj: unknown): obj is UpdateVirtualClipPayload => {
  if (!isObjectV2(obj)) {
    return false;
  }
  const payload = obj as Record<string, unknown>;
  const hasInPoint = 'inPoint' in payload;
  const hasOutPoint = 'outPoint' in payload;
  if (!hasInPoint && !hasOutPoint) {
    return false;
  }
  if (hasInPoint && typeof payload.inPoint !== 'number') {
    return false;
  }
  if (hasOutPoint && typeof payload.outPoint !== 'number') {
    return false;
  }
  return true;
};
